import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Button, Card, Field, inputClass, useAdminApi, useConfirm, useToast } from './ui';

function AdminRegions({ data, reload }) {
  const api = useAdminApi();
  const toast = useToast();
  const confirm = useConfirm();
  const [openId, setOpenId] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const toggleHidden = async (region) => {
    setBusyId(region.id);
    try {
      await api(`/admin/region/${region.id}`, { method: 'PATCH', body: { hide: !region.hide } });
      toast(`${region.name} is now ${region.hide ? 'visible' : 'hidden'}.`);
      await reload({ silent: true });
    } catch (error) {
      toast(error.message, 'error');
    } finally {
      setBusyId(null);
    }
  };

  const deleteRegion = async (region) => {
    const c = region._count;
    const ok = await confirm({
      title: `Delete ${region.name}?`,
      message: `This region has ${c.places} places, ${c.organizers} organizers and ${c.listings} listings. Deleting is permanent, and will be refused if anything still depends on it.`,
      confirmLabel: 'Delete region',
      danger: true,
      requireText: region.name,
    });
    if (!ok) return;
    setBusyId(region.id);
    try {
      await api(`/region/${region.id}`, { method: 'DELETE' });
      toast(`Deleted ${region.name}.`);
      await reload({ silent: true });
    } catch (error) {
      toast(error.message, 'error');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-gray-600 text-left">
            <tr>
              <th className="px-4 py-2 font-medium">Region</th>
              <th className="px-4 py-2 font-medium text-right">Places</th>
              <th className="px-4 py-2 font-medium text-right">Organizers</th>
              <th className="px-4 py-2 font-medium text-right">Listings</th>
              <th className="px-4 py-2 font-medium">Visibility</th>
              <th className="px-4 py-2 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {data.regions.map((region) => (
              <React.Fragment key={region.id}>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <Link to={`/region/${region.urlName}`} className="font-medium hover:underline">{region.name}</Link>
                    <div className="text-xs text-gray-500">/{region.urlName}</div>
                  </td>
                  <td className="px-4 py-3 text-right">{region._count.places}</td>
                  <td className="px-4 py-3 text-right">{region._count.organizers}</td>
                  <td className="px-4 py-3 text-right">{region._count.listings}</td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => toggleHidden(region)}
                      disabled={busyId === region.id}
                      className="disabled:opacity-50"
                      title={region.hide ? 'Click to make visible' : 'Click to hide'}
                    >
                      {region.hide ? <Badge color="purple">Hidden</Badge> : <Badge color="green">Visible</Badge>}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <Button onClick={() => setOpenId(openId === region.id ? null : region.id)}>{openId === region.id ? 'Close' : 'Edit'}</Button>
                    <Button className="ml-2" variant="danger" busy={busyId === region.id} onClick={() => deleteRegion(region)}>Delete</Button>
                  </td>
                </tr>
                {openId === region.id && (
                  <tr>
                    <td colSpan="6" className="bg-gray-50 px-4 py-4">
                      <EditRegion key={region.id} region={region} api={api} toast={toast} reload={reload} onDone={() => setOpenId(null)} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <AddRegion api={api} toast={toast} reload={reload} />
    </div>
  );
}

function EditRegion({ region, api, toast, reload, onDone }) {
  const [form, setForm] = useState({ name: region.name, description: region.description || '', timezone: region.timezone || '' });
  const [saving, setSaving] = useState(false);
  const dirty = form.name !== region.name || form.description !== (region.description || '') || form.timezone !== (region.timezone || '');

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api(`/admin/region/${region.id}`, { method: 'PATCH', body: form });
      toast('Region saved.');
      await reload({ silent: true });
      onDone();
    } catch (error) {
      toast(error.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="grid md:grid-cols-2 gap-4 max-w-3xl">
      <Field label="Name"><input className={inputClass} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
      <Field label="Timezone" hint="e.g. America/Los_Angeles"><input className={inputClass} value={form.timezone} onChange={(e) => setForm({ ...form, timezone: e.target.value })} /></Field>
      <div className="md:col-span-2">
        <Field label="Description"><textarea rows={3} className={inputClass} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
      </div>
      <div className="md:col-span-2 flex gap-2">
        <Button type="submit" variant="primary" busy={saving} disabled={!dirty}>Save</Button>
        <Button type="button" onClick={onDone}>Cancel</Button>
      </div>
    </form>
  );
}

function AddRegion({ api, toast, reload }) {
  const [form, setForm] = useState({ name: '', description: '', hide: false });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api('/region/', { method: 'POST', body: { name: form.name.trim(), description: form.description, hide: form.hide } });
      toast(`Added ${form.name.trim()}${form.hide ? ' (hidden)' : ''}.`);
      setForm({ name: '', description: '', hide: false });
      await reload({ silent: true });
    } catch (error) {
      toast(error.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card title="Add a region">
      <form onSubmit={submit} className="grid md:grid-cols-2 gap-4 max-w-3xl">
        <Field label="Name"><input required className={inputClass} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
        <div className="flex items-end">
          <label className="flex items-center gap-2 text-sm pb-2">
            <input type="checkbox" checked={form.hide} onChange={(e) => setForm({ ...form, hide: e.target.checked })} />
            Start hidden (not shown publicly yet)
          </label>
        </div>
        <div className="md:col-span-2">
          <Field label="Description"><textarea rows={3} className={inputClass} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
        </div>
        <div className="md:col-span-2">
          <Button type="submit" variant="primary" busy={saving} disabled={!form.name.trim()}>Add region</Button>
        </div>
      </form>
    </Card>
  );
}

export default AdminRegions;
